/**
 * Why this clinician (2.6.2): click a placed visit → two stages. Hard filters
 * first, then scores across the feasible set with the weights shown as their
 * configuration. Engine drive times load from the product's schedule options
 * when the tenant is bound; otherwise estimates from the shift-center territory.
 */
import { useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useSchedulerOptionsLazyQuery } from "~/graphql/__generated__/graphql";
import { dateKeyAt, dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { explainPlacement, type EngineSlot } from "../../engine/whyThisClinician";
import { useDemoState } from "../../store/useDemoStore";
import { MoveWithinWindowDialog } from "./MoveWithinWindowDialog";
import { useBranchClock } from "../../components/branchClock";

export function WhyThisClinician({ planVisitId, onClose }: { planVisitId: string; onClose: () => void }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const [engineSlots, setEngineSlots] = useState<EngineSlot[] | undefined>(undefined);
  const [moving, setMoving] = useState(false);
  const [load, { loading, error }] = useSchedulerOptionsLazyQuery({ fetchPolicy: "network-only" });
  const why = useMemo(() => explainPlacement(state, planVisitId, { engineSlots }), [state, planVisitId, engineSlots]);
  if (!why) return null;
  const { visit } = why;
  const b = state.meta.bindings;
  const patientId = b.patientIdsByRef[why.patientRef];
  const visitTypeId = b.visitTypeIdsByKind[visit.visitTypeKind];
  const canLoadEngine = patientId !== undefined && visitTypeId !== undefined && b.serviceLineId !== undefined && state.meta.bound;

  const loadEngine = async () => {
    if (!canLoadEngine) return;
    const start = dateKeyStart(visit.targetDate, tz).toISOString();
    const end = dateKeyAt(visit.targetDate, "23:59", tz).toISOString();
    const target = dateKeyAt(visit.targetDate, visit.windowStart ?? "10:00", tz).toISOString();
    const res = await load({
      variables: {
        patientId: patientId!,
        startTime: start,
        targetTime: target,
        endTime: end,
        serviceLineId: b.serviceLineId!,
        organizationalUnitId: null,
        visitTypeId: visitTypeId!,
        duration: state.config.visitTypes[visit.visitTypeKind].plannedMinutes,
        serviceIds: [],
        providerTraits: [],
        daysOfWeek: null,
        hoursOfDay: null,
        reschedulingVisitId: visit.realVisitId ?? null,
        selectedProviderIds: null,
        useOverflowShifts: false,
      },
    });
    const options = res.data?.scheduleOption.options ?? [];
    const best = new Map<number, EngineSlot>();
    for (const o of options) {
      for (const r of o.resources) {
        const prev = best.get(r.providerId);
        if (!prev || o.scores.totalScore > prev.totalScore) best.set(r.providerId, { providerId: r.providerId, originDriveMinutes: Math.round(o.originDrive), totalScore: Math.round(o.scores.totalScore), driveScore: o.scores.driveScore, proximityScore: o.scores.proximityScore });
      }
    }
    setEngineSlots([...best.values()]);
  };

  const feasible = why.candidates.filter((c) => c.feasible);
  // The drive guardrail still decides eligibility but is never named on screen,
  // so a clinician excluded only by it is simply not listed.
  const shownKeys = new Set(why.filterOrder);
  const excluded = why.candidates
    .filter((c) => !c.feasible)
    .map((c) => ({ ...c, shownFailures: c.filters.filter((f) => !f.pass && shownKeys.has(f.key)) }))
    .filter((c) => c.shownFailures.length > 0);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        Why this clinician
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {why.visitLabel} · {formatInBranchZone(dateKeyStart(visit.targetDate, tz), tz, "EEE MMM d")}
          {visit.windowStart ? ` · ${clock.wall(visit.windowStart)}–${clock.wall(visit.windowEnd ?? visit.windowStart)}` : ""} · {why.patientName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Hard filters
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {excluded.map((c) => {
                const failed = c.shownFailures;
                return (
                  <Tooltip key={c.handle} title={failed.map((f) => `${f.label}: ${f.detail}`).join("\n")}>
                    <Chip size="small" variant="outlined" label={`${c.displayName} · ${c.discipline} — ${failed[0]?.label.toLowerCase()}`} sx={{ opacity: 0.75 }} />
                  </Tooltip>
                );
              })}
              {excluded.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  All clinicians pass the hard filters.
                </Typography>
              )}
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={600}>
                Scores
              </Typography>
              <Chip size="small" variant="outlined" label={`drive time ${why.weights.driveTime} · continuity ${why.weights.continuity}`} />
              {canLoadEngine && !engineSlots && (
                <Button size="small" variant="outlined" onClick={loadEngine} disabled={loading} sx={{ ml: "auto" }}>
                  {loading ? "Loading drive times…" : "Load drive times"}
                </Button>
              )}
              {engineSlots && <Chip size="small" color="info" label={`${engineSlots.length} clinician${engineSlots.length === 1 ? "" : "s"} with engine drive times`} sx={{ ml: "auto" }} />}
              {error && (
                <Typography variant="caption" color="error">
                  Drive times unavailable — showing estimates
                </Typography>
              )}
            </Stack>
            <Table size="small" sx={{ mt: 0.5, "& td, & th": { py: 0.25 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Clinician</TableCell>
                  <TableCell align="right">Drive</TableCell>
                  <TableCell>Continuity</TableCell>
                  <TableCell align="right">Drive score</TableCell>
                  <TableCell align="right">Continuity score</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feasible.map((c) => (
                  <TableRow key={c.handle} selected={c.chosen} sx={{ fontWeight: c.chosen ? 600 : undefined }}>
                    <TableCell>
                      {c.displayName} · {c.discipline}
                      {c.chosen && <Chip size="small" color="primary" label="placed" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right">
                      {c.driveMinutes} min{c.driveIsEstimate ? "*" : ""}
                    </TableCell>
                    <TableCell>{c.continuity.onEpisodeTeam ? "on this episode's team" : c.continuity.visitsWithPatient ? `${c.continuity.visitsWithPatient} prior visit${c.continuity.visitsWithPatient === 1 ? "" : "s"}` : "—"}</TableCell>
                    <TableCell align="right">{c.scores?.drive}</TableCell>
                    <TableCell align="right">{c.scores?.continuity}</TableCell>
                    <TableCell align="right">
                      <b>{c.scores?.total}</b>
                      {c.scores?.engineTotal !== undefined ? ` · engine ${c.scores.engineTotal}` : ""}
                    </TableCell>
                  </TableRow>
                ))}
                {feasible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="caption" color="text.secondary">
                        No clinician passes every hard filter for this visit.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">
              * drive time not yet priced by the router
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setMoving(true)} disabled={visit.status === "completed" || visit.status === "canceled"}>
          Move within window
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {moving && <MoveWithinWindowDialog planVisitId={planVisitId} onClose={() => setMoving(false)} />}
    </Dialog>
  );
}
